import type {
  LineStatus,
  LookupJobStatus,
  ProductOrigin,
  ProductStatus,
  PushOperationKind,
  SyncAction,
} from '@klingerator/shared';

const dateTimeFmt = new Intl.DateTimeFormat('nl-NL', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const dateFmt = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });

export function formatDateTime(v: string | Date | null | undefined): string {
  if (!v) return '—';
  const d = typeof v === 'string' ? new Date(v) : v;
  return isNaN(d.getTime()) ? '—' : dateTimeFmt.format(d);
}

export function formatDate(v: string | Date | null | undefined): string {
  if (!v) return '—';
  const d = typeof v === 'string' ? new Date(v) : v;
  return isNaN(d.getTime()) ? '—' : dateFmt.format(d);
}

export function formatNumber(v: number | string | null | undefined, maxFractionDigits = 4): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return isNaN(n) ? '—' : n.toLocaleString('nl-NL', { maximumFractionDigits: maxFractionDigits });
}

export const STATUS_LABELS: Record<ProductStatus | LineStatus, string> = {
  draft: 'Concept',
  incomplete: 'Onvolledig',
  ready: 'Gereed',
  synced: 'Gesynchroniseerd',
  modified: 'Gewijzigd',
  conflict: 'Conflict',
  sync_error: 'Sync-fout',
  linked: 'Gekoppeld',
  provisional: 'Voorlopig',
  invalid: 'Ongeldig',
};

/** Bootstrap-kleurvariant per status (badge `text-bg-*` en status-dot `bg-*`). */
export const STATUS_VARIANTS: Record<ProductStatus | LineStatus, string> = {
  draft: 'secondary',
  incomplete: 'warning',
  ready: 'primary',
  synced: 'success',
  modified: 'info',
  conflict: 'danger',
  sync_error: 'danger',
  linked: 'success',
  provisional: 'warning',
  invalid: 'danger',
};

export const ORIGIN_LABELS: Record<ProductOrigin, string> = {
  manual: 'Handmatig',
  import: 'Import',
  unit4: 'Unit4',
};

export const ORIGIN_VARIANTS: Record<ProductOrigin, string> = {
  manual: 'light text-dark border',
  import: 'info',
  unit4: 'primary',
};

export function statusLabel(s: string): string {
  return (STATUS_LABELS as Record<string, string>)[s] ?? s;
}

// ---------- Sync / lookup / push (frontend-story 3) ----------

export const SYNC_ACTION_LABELS: Record<SyncAction, string> = {
  load: 'Laden uit Unit4',
  refresh: 'Vernieuwen uit Unit4',
  validate: 'Valideren',
  dry_run: 'Dry-run',
  push: 'Opslaan in Unit4',
  resume: 'Hervatten',
  resolve: 'Conflict opgelost',
  rollback: 'Teruggedraaid',
};

export const SYNC_STATUS_LABELS: Record<'ok' | 'failed' | 'partial', string> = {
  ok: 'Geslaagd',
  failed: 'Mislukt',
  partial: 'Gedeeltelijk',
};
export const SYNC_STATUS_VARIANTS: Record<'ok' | 'failed' | 'partial', string> = {
  ok: 'success',
  failed: 'danger',
  partial: 'warning',
};

export const LOOKUP_STATUS_LABELS: Record<LookupJobStatus, string> = {
  queued: 'In wachtrij',
  running: 'Bezig',
  done: 'Klaar',
  failed: 'Mislukt',
  cancelled: 'Geannuleerd',
};
export const LOOKUP_STATUS_VARIANTS: Record<LookupJobStatus, string> = {
  queued: 'secondary',
  running: 'info',
  done: 'success',
  failed: 'danger',
  cancelled: 'light text-dark border',
};

export const PUSH_KIND_LABELS: Record<PushOperationKind, string> = {
  create_product: 'Product aanmaken',
  update_product: 'Product bijwerken',
  create_composition: 'Regel toevoegen',
  update_composition: 'Regel bijwerken',
  delete_composition: 'Regel verwijderen',
  upsert_note: 'Taalomschrijving bijwerken',
  delete_note: 'Taalomschrijving verwijderen',
  create_article: 'Artikel aanmaken',
  create_supplier: 'Leverancier',
};
export const PUSH_KIND_VARIANTS: Record<PushOperationKind, string> = {
  create_product: 'primary',
  update_product: 'info',
  create_composition: 'success',
  update_composition: 'info',
  delete_composition: 'danger',
  upsert_note: 'info',
  delete_note: 'warning',
  create_article: 'primary',
  create_supplier: 'success',
};

/** Confidence (0..1) → NL label + bootstrap-variant. */
export function confidenceLevel(c: number | null | undefined): {
  label: string;
  variant: string;
  pct: number;
} {
  const n = typeof c === 'number' && !isNaN(c) ? Math.max(0, Math.min(1, c)) : 0;
  const pct = Math.round(n * 100);
  if (n >= 0.8) return { label: 'hoog', variant: 'success', pct };
  if (n >= 0.5) return { label: 'midden', variant: 'warning', pct };
  return { label: 'laag', variant: 'secondary', pct };
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m} min ${s} s`;
}
